import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../utils/api";
import { toast } from "react-toastify";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check session on mount
  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    try {
      const response = await api.get("/auth/session");
      setUser(response.data.user);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post("/auth/login", { email, password });
      setUser(response.data.user);
      toast.success("Welcome back!");
      return { success: true, user: response.data.user };
    } catch (error) {
      const message = error.response?.data?.error || "Login failed";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post("/auth/register", userData);
      toast.success("Registration successful! Please wait for admin approval.");
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.error || "Registration failed";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
      setUser(null);
      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const isAuthenticated = () => {
    return user !== null;
  };

  const isApproved = () => {
    return user?.status === "approved";
  };

  const isAdmin = () => {
    return user?.role === "admin";
  };

  const canSeePrices = () => {
    return isAdmin() || isApproved();
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    checkSession,
    isAuthenticated,
    isApproved,
    isAdmin,
    canSeePrices,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
